import type { SiteModel } from "@/domains/site/site.types";
import type { SiteId } from "@/types/common/ids";
import type { MaybeRef, MaybeRefOrGetter, Ref } from "vue";

import { typedQueryOptions } from "@/lib/query-utils";

export const SiteQueryKeys = {
  all: ["sites"] as const,
  // @deprecated : use detail2()
  detail: (siteId: MaybeRefOrGetter<number>) => [...SiteQueryKeys.all, siteId] as const,
  detail2: (siteId: MaybeRef<SiteId>) => typedQueryOptions<SiteModel>([...SiteQueryKeys.all, siteId] as const),
  search: (query?: unknown) => [...SiteQueryKeys.all, "search", query] as const,
  photos: (siteId: number) => [...SiteQueryKeys.detail(siteId), "photos"] as const,
  photoDetails: (siteId: number, photoId: unknown, ...args: unknown[]) =>
    [...SiteQueryKeys.photos(siteId), photoId, ...args] as const,
  documents: (siteId: number) => [...SiteQueryKeys.detail(siteId), "documents"] as const,
  lists: () => [...SiteQueryKeys.all, "list"] as const,
  details: () => [...SiteQueryKeys.all, "detail"] as const,
  findSitePartnerAddress: (siteId: number) => [...SiteQueryKeys.detail(siteId), "partner-address"] as const,
  updateSitePartnerAddress: (siteId: number) => [...SiteQueryKeys.detail(siteId), "partner-address", "update"] as const,
  mombox: (siteId: number) => [...SiteQueryKeys.detail(siteId), "mombox"] as const,
  modulators: (siteId: number) => [...SiteQueryKeys.detail(siteId), "modulators"] as const,
  lastHardwareConfiguration: (siteId: number) =>
    [...SiteQueryKeys.detail(siteId), "last-hardware-configuration"] as const,
  constraintsSummary: (siteId: number) => [...SiteQueryKeys.detail(siteId), "constraints-summary"] as const,
  workingConditions: (siteId: MaybeRefOrGetter<number>) =>
    [...SiteQueryKeys.detail(siteId), "working-conditions"] as const,
  dsoConsents: (siteId: number) => [...SiteQueryKeys.detail(siteId), "dso-consents"] as const,
  dsoStatus: (siteId: number) => [...SiteQueryKeys.detail(siteId), "dso-status"] as const,
  pushButton: (siteId: number) => [...SiteQueryKeys.detail(siteId), "push-button"] as const,
  lastPhoningEvent: (siteId: number) => [...SiteQueryKeys.detail(siteId), "last-phoning-event"] as const,
  sendSupportMail: (siteId: number) => [...SiteQueryKeys.detail(siteId), "send-support-mail"] as const,
  sendRotationMail: (siteId: number) => [...SiteQueryKeys.detail(siteId), "send-rotation-mail"] as const,
  attachOperation: (siteId: number) => [...SiteQueryKeys.detail(siteId), "attach-operation"] as const,
  findMomboxLastConnectionDate: (siteId: number) =>
    [...SiteQueryKeys.detail(siteId), "mombox", "last-connection-date"] as const,
  updateMomboxVersion: (siteId: number) => [...SiteQueryKeys.detail(siteId), "update-mombox-version"] as const,
  findSiteSensorRequests: (siteId: number) => [...SiteQueryKeys.detail(siteId), "sensors", "requests"] as const,
  sendSiteSensorRequest: (siteId: number) => [...SiteQueryKeys.detail(siteId), "sensors", "send-request"] as const,
  sendAgreementLetter: (siteId: number) => [...SiteQueryKeys.detail(siteId), "send-agreement-letter"] as const,
  findMyVDirectAccessUrl: (siteId: number) => [...SiteQueryKeys.detail(siteId), "find-myv-direct-access"] as const,
  openSitePhoningDirectory: (siteId: MaybeRef<SiteId>) =>
    [...SiteQueryKeys.detail(siteId), "open-site-phoning-directory"] as const,
  findSiteAvailableServiceProviders: (siteId: number) =>
    [...SiteQueryKeys.detail(siteId), "service-providers"] as const,
  siteAppointmentsAvailabilitiesPrefix: (siteId: SiteId) =>
    [...SiteQueryKeys.detail(siteId), "appointment-availabilities"] as const,
  findAppointmentAvailabilities: (siteId: SiteId, args: unknown[]) =>
    [...SiteQueryKeys.siteAppointmentsAvailabilitiesPrefix(siteId), ...args] as const,
  bookAppointment: (siteId: number, args: unknown[]) =>
    [...SiteQueryKeys.detail(siteId), "book-appointment", ...args] as const,
  bookManualForcedAppointment: (siteId: number, args: unknown[]) =>
    [...SiteQueryKeys.detail(siteId), "book-manual-forced-appointment", ...args] as const,
  bookFlexAppointment: (siteId: number, args: unknown[]) =>
    [...SiteQueryKeys.detail(siteId), "book-flex-appointment", ...args] as const,
  siteAppointmentsPrefix: (siteId: SiteId) => [...SiteQueryKeys.detail(siteId), "appointments"] as const,
  findSiteLastAppointments: (siteId: SiteId, from: string) =>
    [...SiteQueryKeys.siteAppointmentsPrefix(siteId), { from }] as const,
  findAppointmentCancellationReasonsById: (siteId: SiteId, reasonTypeId: number) =>
    [...SiteQueryKeys.siteAppointmentsPrefix(siteId), "cancellation-reasons", reasonTypeId] as const,
  cancelSiteAppointment: (siteId: SiteId, appId: number) =>
    [...SiteQueryKeys.siteAppointmentsPrefix(siteId), appId, "cancel"] as const,
  updateSiteEligibleApplianceNumber: (siteId: number) =>
    [...SiteQueryKeys.detail(siteId), "eligible-appliance-number", "update"] as const,
  findSiteAppointmentsPreparation: (siteId: Ref<SiteId>) =>
    [...SiteQueryKeys.detail2(siteId), "appointments-preparation"] as const,
  findSiteOffersWithConditions: (siteId: Ref<SiteId>) =>
    [...SiteQueryKeys.detail2(siteId), "offers-with-conditions"] as const,
  findSiteAlerts: (siteId: number) => [...SiteQueryKeys.detail(siteId), "alerts"] as const,
  findSiteEvents: (siteId: number) => [...SiteQueryKeys.detail(siteId), "events"] as const,
  closePhoningDirectory: (siteId: number) => [...SiteQueryKeys.detail(siteId), "close-phoning-directory"] as const,
  attachSubscriberToSite: (siteId: number) => [...SiteQueryKeys.detail(siteId), "attach-subscriber-to-site"] as const,
  findSiteInstallationFlow: (siteId: number) =>
    [...SiteQueryKeys.detail(siteId), "find-site-installation-flow"] as const,
  findSiteInstallationStatus: (siteId: number) =>
    [...SiteQueryKeys.detail(siteId), "find-site-installation-status"] as const,
  findRefTownInfo: (zip: unknown) => [...SiteQueryKeys.all, "find-ref-town-info", zip] as const,
  createSite: () => [...SiteQueryKeys.all, "create-site"] as const,
  updateSite: (siteId: MaybeRef<SiteId>) => [...SiteQueryKeys.detail2(siteId), "update-site"] as const,
  replaceSiteAddress: (siteId: MaybeRef<SiteId>) => [...SiteQueryKeys.detail2(siteId), "replace-site-address"] as const,
  partiallyUpdateSiteAddress: (siteId: MaybeRef<SiteId>) =>
    [...SiteQueryKeys.detail2(siteId), "partially-update-site-address"] as const,
  // deprecated
  updateSitePostalAddress: (siteId: number) => [...SiteQueryKeys.detail(siteId), "update-site-postal-address"] as const,
  // deprecated
  updateSiteGeocodedAddress: (siteId: number) =>
    [...SiteQueryKeys.detail(siteId), "update-site-geocoded-address"] as const,
  bookManualAppointment: (siteId: number, ...args: unknown[]) =>
    [...SiteQueryKeys.detail(siteId), "book-manual-appointment", ...args] as const,
  uploadSitePhoto: (siteId: number, ...args: unknown[]) =>
    [...SiteQueryKeys.detail(siteId), "upload-site-photo", ...args] as const,
  findSiteFirstServiceProvider: (siteId: number, ...args: unknown[]) =>
    [...SiteQueryKeys.detail(siteId), "first-service-provider", ...args] as const,
  updateSubscriberOffer: (siteId: number) => [siteId, "update-subscriber-offer"] as const,
};

export const SiteMutationKeys = {
  all: [{ api: "backoffice", type: "mutation", scope: "site" }] as const,
  sendDocumentByMailToSubscriber: () =>
    [
      {
        ...SiteMutationKeys.all[0],
        action: "send-document-by-mail-to-subscriber",
      },
    ] as const,
};
